/**
 * CollectPaymentSummary -- COLLECT-PAYMENT-V1, the head of the patient
 * collection screen: WHO IS PAYING, said once, and the three money figures
 * that visit owes, shows and has paid.
 *
 * WHAT THIS IS FOR. The billing door used to state the patient in one place,
 * the visit in another, and the money as a flat definition list in a third,
 * with no number for what is still outstanding anywhere on the screen. The
 * approved composition puts identity ONCE and the three figures together,
 * above the form, in the first viewport.
 *
 * RENDER-ONLY, AND IT COMPUTES NO MONEY. Every figure is read off the
 * checkout-handoff wire, which the server derives through
 * `deriveEpisodeQueueState` (lib/billing-queue.ts:163-189) -- THE ONE
 * netting home. This component performs no subtraction of its own, invents
 * no discount or allocation rule, and renders NO NUMBER where the law
 * yields none.
 *
 * THE NULL-AMOUNT OBLIGATION LAW, honoured here: an accepted obligation
 * whose items carry no configured value has an outstanding of UNKNOWN size.
 * It is never Rs 0 and never a "Collect Rs 0" tile -- the tile says what is
 * true instead, and the offer control on the form does not render at all.
 */

import {
  formatMinorUnitsInr,
} from '@/dashboard/billing/billing-entry-view';
// FROM THE TYPE'S OWN HOME, not from the module that merely re-imports it.
// `api.ts` pulls `CheckoutHandoffResponse` in for its own signatures and does
// not re-export it; `tsc -b` says so (TS2459) while a bare `tsc --noEmit -p
// tsconfig.json` does not -- which is how this shipped red. Run the repo's
// own `npm run type-check`.
import { treatmentEstimateOutstanding, type CheckoutHandoffResponse } from '@/dashboard/billing/checkout-handoff-view';
// THE SHIPPED PRIMITIVES, not local lookalikes. A raw <button> in a new
// dashboard file is a conformance failure by construction
// (design-system-conformance-guard RULE 3); a local tile beside KpiTile is
// the same failure one level up. KpiTile already IS this shape -- uppercase
// micro label, tabular-nums metric, plain sub line, tone as a tinted surface
// with a 3px inset edge -- and it is styled in dashboard.css, so adopting it
// deletes a component AND the CSS that propped it up.
import {formatTimestampIst} from '@/lib/datetime-ist';
import { Link } from 'react-router-dom';
import AcButton from '@/dashboard/ui/AcButton';
import './CollectPayment.css';

export interface CollectPaymentSummaryProps {
  readonly handoff: CheckoutHandoffResponse;
  readonly patientName: string;
  /** Existing record door, supplied only when the viewer may open it. */
  readonly patientRecordPath?: string | null;
  readonly patientUmr: string | null;
  readonly clinicLabel: string;
  /** Rendered as the identity's own change affordance; omitted when the
   *  caller has no patient-swap surface to offer. */
  readonly onChangePatient?: () => void;
  readonly onRemoveVisitLink?: () => void;
  readonly fromDayBoard: boolean;
}

export default function CollectPaymentSummary({
  handoff,
  patientName,
  patientRecordPath = null,
  patientUmr,
  clinicLabel,
  onChangePatient,
  onRemoveVisitLink,
}: CollectPaymentSummaryProps): JSX.Element {
  const accepted = handoff.acceptedValuedSumInr;
  const collected = handoff.collectedMinorUnits;
  const outstanding = treatmentEstimateOutstanding(handoff);

  const acceptedValue =
    accepted !== null
      ? formatMinorUnitsInr(accepted * 100)
      : 'Unavailable';
  // THE OUTSTANDING TILE, and the only place this screen speaks about a
  // remainder. Three distinct truths, never collapsed into a zero:
  //   a positive number  -> the visit owes it;
  //   nothing owed       -> say settled, do not print Rs 0 as a debt;
  //   unknown size       -> say so; the NULL-AMOUNT law forbids a number.
  const outstandingIsNumber = outstanding !== null && outstanding > 0;
  const outstandingUnknown =
    outstanding === null;
  const outstandingValue = outstandingIsNumber
    ? formatMinorUnitsInr(outstanding)
    : outstandingUnknown
      ? 'Unavailable'
      : 'Nothing outstanding';
  return <section className="quiet-payment-summary" aria-label="Patient and visit" data-testid="collect-who-is-paying">
    <div className="quiet-payment-identity">
      <div><strong data-testid="collect-patient-name">{patientRecordPath ? <Link to={patientRecordPath}>{patientName}</Link> : patientName}</strong>{patientUmr ? <span className="ac-collect__umr">{patientUmr}</span> : null}
      <p>{clinicLabel} · Visit #{handoff.appointmentId}</p></div>
      {onChangePatient ? <AcButton variant="ghost" size="sm" onClick={onChangePatient}>Change patient</AcButton> : null}
    </div>
    <div className="quiet-payment-summary__heading">Visit #{handoff.appointmentId} <span>· {handoff.appointmentTimeIso?formatTimestampIst(handoff.appointmentTimeIso):'Date unavailable'}</span> <span>· Estimate</span></div>
    <dl className="quiet-payment-figures">
      <div><dt>Accepted</dt><dd>{acceptedValue}</dd></div>
      <div><dt>Received</dt><dd>{collected > 0 ? formatMinorUnitsInr(collected) : 'Nothing yet'}</dd></div>
      <div><dt>Outstanding</dt><dd>{outstandingValue}</dd></div>
    </dl>
    {outstandingUnknown ? <p role="alert">Treatment amount unavailable. Confirm the amount with the doctor.</p> : null}
    {onRemoveVisitLink ? <details className="quiet-visit-options"><summary>Visit options</summary><AcButton variant="ghost" size="sm" onClick={onRemoveVisitLink} data-testid="collect-remove-link">Remove visit link</AcButton></details> : null}
  </section>;
}
